import type { EventDescription } from '~/composables/types/event'
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { sanitizeAlgoliaQueryId } from '~/utils/helpers'
import { getAlgoliaMerchandisingIndexForEventDescription } from '~/utils/algoliaHelpers'

interface PurchaseEventItem {
  eventDescriptionId: string
  queryID?: string
  price?: number
  quantity?: number
  indexName?: string
}

export const useAlgoliaConversion = () => {
  const { locale } = useI18n()
  const route = useRoute()
  const { client: algoliaInsightsClient, token } = useAlgoliaSearchInsights()

  const sendPurchaseEvent = (items: PurchaseEventItem[]) => {
    /* @see https://www.algolia.com/doc/libraries/search-insights/purchased-object-ids-after-search */
    const validItems = items?.filter((item) => item != null) || []

    if (!validItems || validItems.length === 0) {
      console.error('sendPurchaseEvent: no valid items provided')
      return
    }

    // Group items by their target Algolia index so kits (products_*) and
    // events (events_*) are reported to the correct index separately
    const defaultIndex = getEventListIndexName(locale.value)
    const byIndex = new Map<string, PurchaseEventItem[]>()
    validItems.forEach((item) => {
      const idx = item.indexName || defaultIndex
      const group = byIndex.get(idx) || []
      group.push(item)
      byIndex.set(idx, group)
    })

    byIndex.forEach((groupItems, indexName) => {
      const objectIDs = groupItems.map(
        (item) => 'EventDescription::' + item.eventDescriptionId
      )
      const objectData = groupItems.map((item) => {
        const data: any = {}
        const sanitizedQueryID = sanitizeAlgoliaQueryId(item.queryID)
        if (sanitizedQueryID) {
          data.queryID = sanitizedQueryID
        }
        if (item.price !== undefined) {
          data.price = item.price
        }
        if (item.quantity !== undefined) {
          data.quantity = item.quantity
        }
        return data
      })

      try {
        algoliaInsightsClient('purchasedObjectIDsAfterSearch', {
          userToken: token.value,
          index: indexName,
          eventName: 'Product Purchased',
          objectIDs,
          objectData,
          currency: 'EUR',
        })
      } catch (e) {
        console.error(e)
      }
    })
  }

  const sendAddToCartEvent = (event: EventDescription, indexName?: string) => {
    const rawQueryId = route?.query?.qid?.toString() || null
    const queryId = sanitizeAlgoliaQueryId(rawQueryId)
    const algoliaInsightsMethod = queryId
      ? 'addedToCartObjectIDsAfterSearch'
      : 'addedToCartObjectIDs'

    try {
      algoliaInsightsClient(algoliaInsightsMethod, {
        userToken: token.value,
        index: getAlgoliaMerchandisingIndexForEventDescription(
          event,
          locale.value,
          indexName
        ),
        eventName: 'Event added to cart - Event page',
        ...(queryId && {
          queryId,
        }),
        objectIDs: ['EventDescription::' + eventGetters.getId(event)],
      })
    } catch (e) {
      console.error(e)
    }
  }
  const sendViewEvents = (event: EventDescription, indexName?: string) => {
    const rawQueryId = route.query.qid?.toString() || null
    const queryId = sanitizeAlgoliaQueryId(rawQueryId)

    if (token.value) {
      try {
        /* @see https://www.algolia.com/doc/api-reference/api-methods/viewed-object-ids/ */
        algoliaInsightsClient('viewedObjectIDs', {
          userToken: token.value,
          ...(queryId && {
            queryId,
          }),
          index: getAlgoliaMerchandisingIndexForEventDescription(
            event,
            locale.value,
            indexName
          ),
          eventName: 'Event Viewed - Event page',
          objectIDs: ['EventDescription::' + eventGetters.getId(event)],
        })
      } catch (e) {
        console.error(e)
      }
    }
  }

  const sendViewedObjectIDs = (
    items: AlgoliaEventItem[],
    eventName: string,
    indexName?: string
  ) => {
    if (!token.value || !items || items.length === 0) {
      return
    }

    // Limit to 20 items as per Algolia documentation
    const limitedItems = items.slice(0, 20)
    const objectIDs = limitedItems.map(
      (item) => 'EventDescription::' + algoliaEventGetters.fnGetHashedId(item)
    )

    try {
      algoliaInsightsClient('viewedObjectIDs', {
        userToken: token.value,
        index: indexName || getEventListIndexName(locale.value),
        eventName,
        objectIDs,
      })
    } catch (e) {
      console.error(e)
    }
  }

  return {
    sendPurchaseEvent,
    sendAddToCartEvent,
    sendViewEvents,
    sendViewedObjectIDs,
  }
}
